import { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { ClipboardCheck, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, fmtEuro } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Badge } from "@/components/warehouse/PurchaseOrders";
import { WarehouseSelect } from "@/components/warehouse/StockTab";
import { CountForm } from "@/components/warehouse/CountForm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const COUNT_STATUS = { OPEN: ["In conta", "bg-amber-100 text-amber-900"], SUBMITTED: ["Da approvare", "bg-sky-100 text-sky-900"],
  APPROVED: ["Approvato", "bg-emerald-100 text-emerald-900"], CANCELLED: ["Annullato", "bg-zinc-100 text-zinc-600"] };

const Totals = ({ c }) => (
  <div className="mt-3 grid grid-cols-3 gap-3" data-testid={`count-totals-${c.number}`}>
    {[["Ammanchi", c.value_missing, "text-red-700", "missing"], ["Eccedenze", c.value_extra, "text-emerald-700", "extra"], ["Netto", c.value_net, c.value_net < 0 ? "text-red-700" : "text-emerald-700", "net"]].map(([l, v, cls, k]) => (
      <div key={l} className="border bg-white px-3 py-2"><div className="micro-label">{l}</div><div className={`font-mono text-lg font-semibold ${cls}`} data-testid={`count-total-${k}`}>{fmtEuro(k === "missing" ? (v ? -v : 0) : v || 0)}</div></div>
    ))}
  </div>
);

const Summary = ({ refreshKey, warehouses }) => {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const { companyId } = useData();
  const [raw, setData] = useState(null);
  const data = useMemo(() => {
    if (!raw || !companyId) return raw;
    const ids = new Set(warehouses.map((w) => w.id));
    const rows = raw.rows.filter((r) => ids.has(r.warehouse_id));
    const sum = (k) => Math.round(rows.reduce((s, r) => s + r[k], 0) * 100) / 100;
    return { rows, totals: { value_missing: sum("value_missing"), value_extra: sum("value_extra"), value_net: sum("value_net") } };
  }, [raw, warehouses, companyId]);
  useEffect(() => { api.get("/warehouse/counts-summary", { params: { year } }).then((r) => setData(r.data)).catch(() => {}); }, [year, refreshKey]);
  const years = Array.from({ length: 3 }, (_, i) => String(new Date().getFullYear() - i));
  return (
    <section className="mb-6" data-testid="counts-summary">
      <div className="mb-2 flex items-center gap-3">
        <div className="micro-label">Differenze inventariali per magazzino e mese</div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="h-8 w-28 bg-white" data-testid="counts-summary-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        {data && <span className="ml-auto font-mono text-sm" data-testid="counts-summary-total">Netto anno: <strong className={data.totals.value_net < 0 ? "text-red-700" : "text-emerald-700"}>{fmtEuro(data.totals.value_net)}</strong></span>}
      </div>
      {!data?.rows.length ? <p className="border bg-white p-3 text-sm text-muted-foreground">Nessun inventario approvato nell'anno.</p> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Mese</TableHead><TableHead>Magazzino</TableHead><TableHead className="text-right">Inventari</TableHead><TableHead className="text-right">Ammanchi</TableHead><TableHead className="text-right">Eccedenze</TableHead><TableHead className="text-right">Netto</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={`${r.month}${r.warehouse_id}`} data-testid={`counts-summary-row-${r.month}-${r.warehouse_id}`}>
                  <TableCell className="font-mono text-xs">{r.month.slice(5)}/{r.month.slice(0, 4)}</TableCell><TableCell className="text-sm">{r.warehouse_name}</TableCell>
                  <TableCell className="text-right font-mono">{r.counts}</TableCell><TableCell className="text-right font-mono text-red-700">{fmtEuro(r.value_missing ? -r.value_missing : 0)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{fmtEuro(r.value_extra)}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${r.value_net < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtEuro(r.value_net)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
};

const Review = ({ count, onDone }) => {
  const { can } = useAuth();
  const [note, setNote] = useState("");
  const act = async (fn, msg) => {
    try {
      const r = await fn();
      toast.success(r.data.adjustments != null ? `${msg}: ${r.data.adjustments} rettifiche registrate` : msg);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const diffs = count.lines.filter((l) => l.diff || l.missing_serials?.length || l.extra_serials?.length);
  return (
    <div data-testid={`count-review-${count.number}`}>
      <div className="overflow-x-auto border bg-white">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead>Articolo</TableHead><TableHead className="text-right">Atteso</TableHead><TableHead className="text-right">Contato</TableHead><TableHead className="text-right">Differenza</TableHead><TableHead className="text-right">Valore</TableHead><TableHead>Seriali</TableHead></TableRow></TableHeader>
          <TableBody>
            {count.lines.map((l) => (
              <TableRow key={l.item_id} className={l.diff ? "bg-amber-50/60" : ""} data-testid={`count-review-line-${l.code}`}>
                <TableCell className="text-sm"><span className="font-mono text-xs font-semibold">{l.code}</span> {l.name}</TableCell>
                <TableCell className="text-right font-mono">{l.expected_qty}</TableCell>
                <TableCell className="text-right font-mono">{l.counted_qty ?? <span className="text-xs text-muted-foreground">non contato</span>}</TableCell>
                <TableCell className={`text-right font-mono font-semibold ${l.diff < 0 ? "text-red-700" : l.diff > 0 ? "text-emerald-700" : ""}`} data-testid={`count-diff-${l.code}`}>{l.diff == null ? "—" : l.diff > 0 ? `+${l.diff}` : l.diff}</TableCell>
                <TableCell className={`text-right font-mono text-xs ${l.value < 0 ? "text-red-700" : l.value > 0 ? "text-emerald-700" : ""}`} data-testid={`count-value-${l.code}`}>{l.value ? fmtEuro(l.value) : "—"}</TableCell>
                <TableCell className="font-mono text-[11px]">
                  {l.missing_serials?.length > 0 && <div className="text-red-700">Mancanti: {l.missing_serials.join(", ")}</div>}
                  {l.extra_serials?.length > 0 && <div className="text-emerald-700">In più: {l.extra_serials.join(", ")}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Totals c={count} />
      <p className="mt-2 text-xs text-muted-foreground">{diffs.length} articoli con differenze, valorizzate al costo unitario dell'articolo. Le rettifiche si calcolano sulla giacenza all'apertura della conta.</p>
      {count.status === "SUBMITTED" && can("inventory:approve") && (
        <div className="mt-4 space-y-2">
          <Textarea placeholder="Motivo per far ricontare (obbligatorio se respingi)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="count-reject-note" />
          <div className="flex gap-2">
            <Button onClick={() => act(() => api.post(`/warehouse/counts/${count.id}/approve`), "Inventario approvato")} data-testid="count-approve-button">Approva e rettifica giacenze</Button>
            <Button variant="outline" disabled={!note.trim()} onClick={() => act(() => api.post(`/warehouse/counts/${count.id}/reject`, { note }), "Conta rimandata in ricontrollo")} data-testid="count-reject-button">Fai ricontare</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const CountDialog = ({ count, onClose, onDone, onReload }) => {
  if (!count) return null;
  const last = count.history?.filter((h) => h.action === "rejected").pop();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="count-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{count.number} · {count.warehouse_name}</DialogTitle></DialogHeader>
        {last && count.status === "OPEN" && <p className="border-l-4 border-red-600 bg-red-50 px-3 py-2 text-sm" data-testid="count-rejected-note">Da ricontare: {last.note} ({last.by})</p>}
        {count.status === "OPEN" ? <CountForm count={count} onReload={onReload} onDone={(submitted) => { onDone(); if (submitted) onClose(); }} /> : <Review count={count} onDone={() => { onDone(); onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
};

export const CountsTab = ({ warehouses, onChanged }) => {
  const { can } = useAuth();
  const { companyId } = useData();
  const [raw, setRows] = useState([]);
  const rows = companyId ? raw.filter((c) => warehouses.some((w) => w.id === c.warehouse_id)) : raw;
  const [opening, setOpening] = useState(false);
  const [wh, setWh] = useState("");
  useEffect(() => { setWh(""); }, [companyId]);
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const load = useCallback(() => { setRefreshKey((k) => k + 1); return api.get("/warehouse/counts").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const open = async () => {
    try {
      const r = await api.post("/warehouse/counts", { warehouse_id: wh });
      toast.success(`Inventario ${r.data.number} aperto`);
      setOpening(false);
      setWh("");
      load();
      if (r.data.kind !== "VAN") setSelected(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pick = (c) => api.get(`/warehouse/counts/${c.id}`).then((r) => setSelected(r.data)).catch((e) => toast.error(errMsg(e)));
  const cancel = async (c) => {
    try {
      await api.post(`/warehouse/counts/${c.id}/cancel`);
      toast.success("Inventario annullato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="counts-tab">
      <Summary refreshKey={refreshKey} warehouses={warehouses} />
      <div className="mb-3 flex items-center gap-3">
        <p className="text-sm text-muted-foreground">Le conte sui furgoni compaiono anche nell'app del tecnico della squadra. Ogni primo lunedì del mese alle 7:00 si apre in automatico la conta di tutti i furgoni.</p>
        {can("inventory:write") && <Button className="ml-auto" onClick={() => setOpening(true)} data-testid="count-new-button"><Plus className="mr-1 h-4 w-4" />Nuovo inventario</Button>}
      </div>
      {rows.length === 0 ? <Empty text="Nessun inventario" testId="counts-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Numero</TableHead><TableHead>Magazzino</TableHead><TableHead>Stato</TableHead><TableHead>Righe contate</TableHead><TableHead className="text-right">Valore netto</TableHead><TableHead>Aperto</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`count-row-${c.number}`}>
                  <TableCell className="font-mono text-sm font-semibold">{c.number}</TableCell>
                  <TableCell className="text-sm">{c.warehouse_name}</TableCell>
                  <TableCell><Badge map={COUNT_STATUS} value={c.status} testId={`count-status-${c.number}`} /></TableCell>
                  <TableCell className="font-mono text-xs">{c.counted_lines}/{c.lines.length}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${c.value_net < 0 ? "text-red-700" : ""}`} data-testid={`count-net-${c.number}`}>{c.value_net != null && c.status !== "CANCELLED" ? fmtEuro(c.value_net) : "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDateTime(c.opened_at)} · {c.opened_by}{c.source === "AUTO" && <span className="ml-1 text-[10px] font-semibold uppercase text-muted-foreground">mensile</span>}</TableCell>
                  <TableCell className="text-right">
                    {c.status !== "CANCELLED" && <Button size="sm" variant="outline" className="mr-1" onClick={() => pick(c)} data-testid={`count-open-${c.number}`}><ClipboardCheck className="mr-1 h-3.5 w-3.5" />{c.status === "OPEN" ? "Conta" : c.status === "SUBMITTED" ? "Rivedi" : "Dettaglio"}</Button>}
                    {["OPEN", "SUBMITTED"].includes(c.status) && can("inventory:write") && <Button size="sm" variant="ghost" onClick={() => cancel(c)} data-testid={`count-cancel-${c.number}`}>Annulla</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={opening} onOpenChange={setOpening}>
        <DialogContent data-testid="count-open-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo inventario</DialogTitle></DialogHeader>
          <WarehouseSelect value={wh} onChange={setWh} warehouses={warehouses} testId="count-warehouse-select" className="w-full" />
          <DialogFooter><Button onClick={open} disabled={!wh} data-testid="count-open-confirm">Apri conta</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <CountDialog count={selected} onReload={() => pick(selected)} onClose={() => setSelected(null)} onDone={() => { load(); onChanged(); }} />
    </div>
  );
};
